import { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { Button } from "@lumanity/ui/primitives";
import OutletCombobox from "./OutletCombobox";
import OutletGuidelinesForm from "./OutletGuidelinesForm";
import { OUTLET_KIND_LABELS, normalizeGuidelines, sameJson } from "./model";
import type { BriefStepProps } from "./briefSteps";
import type { Outlet, OutputPatch } from "./types";

/** Brief step 1 (FR-4-02..05): the journal or congress, and its guidelines as the writer will follow them. */
const TargetOutletStep = ({ output, save, saving, onDirtyChange, onSkip, onContinue }: BriefStepProps & { onSkip: () => void }) => {
  const [initialGuidelines] = useState(() => normalizeGuidelines(output.guidelines));
  const [outlet, setOutlet] = useState<Outlet | null>(output.outlet);
  const [guidelines, setGuidelines] = useState(initialGuidelines);

  const outletChanged = (outlet?.id ?? null) !== output.outlet_id;
  const dirty = outletChanged || (!!outlet && !sameJson(guidelines, initialGuidelines));
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const patch = (): Omit<OutputPatch, "version"> =>
    outlet ? { outlet_id: outlet.id, guidelines } : { outlet_id: null };

  const chooseOutlet = (next: Outlet) => {
    setOutlet(next);
    // A newly chosen outlet starts from its own guidelines; re-picking the saved one keeps the saved overrides.
    setGuidelines(next.id === output.outlet_id ? initialGuidelines : normalizeGuidelines(next.guidelines));
  };

  const clearOutlet = () => {
    setOutlet(null);
    setGuidelines(initialGuidelines);
  };

  const handleSave = () => void save(patch(), { toastOnSuccess: true });
  const handleContinue = async () => {
    if (dirty && !(await save(patch()))) return;
    onContinue();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-brand-navy">What is your target outlet?</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose a journal or congress you are aiming for with this publication. If venue has not been confirmed yet you can
          Skip and select the venue later in the flow.
        </p>
      </div>

      <div className="space-y-2">
        <OutletCombobox value={outlet} onChange={chooseOutlet} disabled={saving} />
        {outlet && (
          <div className="flex items-center justify-between gap-3 bg-brand-light-teal px-3 py-2 text-sm">
            <span className="min-w-0 truncate">
              <span className="font-semibold text-brand-navy">{outlet.name}</span>
              <span className="text-muted-foreground">
                {" "}
                · {OUTLET_KIND_LABELS[outlet.kind]}
                {outlet.publisher && ` · ${outlet.publisher}`}
              </span>
            </span>
            <Button type="button" variant="ghost" size="sm" onClick={clearOutlet} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Remove
            </Button>
          </div>
        )}
      </div>

      {outlet && (
        <section className="space-y-4 border-t pt-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-brand-navy">Target outlet guidelines</h3>
          <OutletGuidelinesForm value={guidelines} onChange={setGuidelines} disabled={saving} idPrefix="output-guidelines" />
        </section>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-5">
        <Button type="button" variant="ghost" onClick={onSkip} disabled={saving}>
          Skip
        </Button>
        <Button type="button" variant="outline" onClick={handleSave} disabled={saving || !dirty}>
          Save Draft
        </Button>
        <Button type="button" className="bg-brand-teal hover:bg-brand-teal/90 text-white" onClick={() => void handleContinue()} disabled={saving}>
          Continue <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default TargetOutletStep;
